import './index.css';
// import './providers/wdyr/index.ts';
import './providers/i18n';

import LogoSvg from '@icons/logo.svg?react';
import * as Sentry from '@sentry/react';
import { DeviceModeProvider } from '@shared/deviceMode/DeviceModeProvider.tsx';
import { applyThemeColors } from '@shared/hooks/useDynamicThemeColor.ts';
import { installChunkErrorHandler } from '@shared/utils/installChunkErrorHandler';
import {
  containsCheckInTokenPath,
  redactCheckInTokens,
} from '@shared/utils/sentryCheckInRedaction.ts';
import { shouldUseServiceWorker } from '@shared/utils/serviceWorker';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router/dom';

import { AppWrapper } from './providers/AppWrapper.tsx';
import index from './providers/routes';
import { store } from './store/store.ts';

installChunkErrorHandler();

Sentry.init({
  dsn: 'https://847a14a71d0aee7235c98fa5fec6b357@o4507372362268672.ingest.us.sentry.io/4507374823604224',
  integrations: [Sentry.browserTracingIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.1,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/cgt-finance\.fly\.dev/],
  ignoreErrors: ['<unknown>'],
  beforeSend(event) {
    return redactCheckInTokens(event);
  },
  beforeSendTransaction(event) {
    if (
      containsCheckInTokenPath(event.request?.url) ||
      containsCheckInTokenPath(event.transaction)
    ) {
      return null;
    }
    return redactCheckInTokens(event);
  },
  beforeSendSpan(span) {
    return redactCheckInTokens(span);
  },
  beforeBreadcrumb(breadcrumb) {
    return redactCheckInTokens(breadcrumb);
  },
});

// biome-ignore lint/style/noNonNullAssertion: <explanation>
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <DeviceModeProvider>
        <AppWrapper>
          <Suspense
            fallback={
              <div
                role="status"
                aria-label="Loading"
                className="flex min-h-screen w-screen items-center justify-center gap-3 bg-base-300 text-2xl"
              >
                <LogoSvg className="fill-current h-8 w-8" aria-hidden="true" />
                <span className="loading loading-spinner loading-lg" aria-hidden="true" />
                <span>Loading...</span>
              </div>
            }
          >
            <RouterProvider router={index} />
          </Suspense>
        </AppWrapper>
      </DeviceModeProvider>
    </Provider>
  </React.StrictMode>,
);

window.addEventListener('load', async () => {
  try {
    // Apply initial theme colors for PWA
    const savedTheme = localStorage.getItem('theme') ?? 'light';
    applyThemeColors(savedTheme);

    // Only register the app's production service worker.
    if (!shouldUseServiceWorker() || !('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      updateViaCache: 'none',
    });
    console.log('Service Worker registered successfully:', registration);

    // Listen for service worker updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker is available
            console.log('New service worker available');
            // You could show a notification to the user here
          }
        });
      }
    });

    // Handle service worker messages
    navigator.serviceWorker.addEventListener('message', event => {
      console.log('Message from service worker:', event.data);
    });
  } catch (registrationError) {
    console.error('SW registration failed:', registrationError);
  }
});
